import { useState, ChangeEvent, FormEvent } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { logger } from '@/shared/utils';
import { useLogin } from '../features/user';
import { useUserState } from '../features/user/operations/UserStateProvider';
import { FormError, Input } from '@/shared/components/ui';
import { APP_CONFIG, ROUTES } from '@/shared/config';
import { useSafeThemeClasses } from '@/shared/theme';
import toast from 'react-hot-toast';

interface FormData {
	email: string; // Can be email or username
	password: string;
}

interface FormErrors {
	[key: string]: string;
}

function LoginPage() {
	const [formData, setFormData] = useState<FormData>({ email: '', password: '' });
	const [errors, setErrors] = useState<FormErrors>({});
	const [isSubmitting, setIsSubmitting] = useState(false);
	const navigate = useNavigate();
	
	// Use safe theme theme classes
	const theme = useSafeThemeClasses();
	
	// Use the new isolated login operation
	const { login, error: loginError } = useLogin();
	const { refreshUser } = useUserState();

	// Handle input changes
	const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
		setFormData({
			...formData,
			[e.target.name]: e.target.value,
		});
	};

	// Form validation
	const validateForm = () => {
		const formErrors: FormErrors = {};
		if (!formData.email.trim()) {
			formErrors.email = 'Username or email is required';
		}
		if (!formData.password) {
			formErrors.password = 'Password is required';
		}

		setErrors(formErrors);
		return Object.keys(formErrors).length === 0;
	};

	// Form submission
	const handleSubmit = async (e: FormEvent) => {
		e.preventDefault();

		if (!validateForm()) {
			return;
		}

		setIsSubmitting(true);
		logger.info('User login attempt', 'USER', { email: formData.email });

		try {
			// Use the new isolated login operation
			const user = await login({
				email: formData.email,
				password: formData.password,
			});
			
			if (user) {
				// Login was successful
				logger.info('User login successful', 'USER', {
					email: formData.email,
					userEmail: user.email
				});

				// Refresh user state to ensure all data is up to date
				await refreshUser();

				// Show success toaster
				toast.success('🎉 Welcome back! Login successful!');

				setFormData({ email: '', password: '' });
				setErrors({});
				navigate('/dashboard');
			} else {
				// Login failed but no error was thrown
				toast.error('❌ Login failed. Please check your credentials.');
			}
		} catch (error) {
			logger.error('User login failed', 'USER', { error });
			
			// Check if error is about inactive account
			const errorMessage = error instanceof Error ? error.message : String(error);
			if (errorMessage.includes('pending admin approval') || errorMessage.includes('inactive')) {
				toast.error('❌ Your account is pending admin approval. Please contact an administrator.');
			} else {
				// Show error toaster
				toast.error('❌ Login failed. Please try again.');
			}
		}

		setIsSubmitting(false);
	};

	return (
		<div className={`min-h-screen flex items-center justify-center p-4 ${theme.background} relative overflow-hidden`}>
			{/* Background decoration */}
			<div className='fixed inset-0 overflow-hidden pointer-events-none'>
				<div className={`absolute -top-40 -right-40 w-80 h-80 rounded-full blur-3xl ${theme.orbs[0]} animate-pulse`}></div>
				<div className={`absolute -bottom-40 -left-40 w-80 h-80 rounded-full blur-3xl ${theme.orbs[1]} animate-pulse`} style={{ animationDelay: '1s' }}></div>
			</div>

			{/* Main container */}
			<div className='relative w-full max-w-md z-10'>
				{/* Glass morphism card - Enhanced */}
				<div className={`${theme.card} p-8 space-y-6`}>
					{/* Header */}
					<div className='text-center space-y-2'>
						<h1 className={`text-3xl font-bold ${theme.textHeading} leading-tight tracking-tight`}>
							Welcome Back
						</h1>
						<p className={`${theme.textSecondary} text-sm leading-relaxed`}>
							Sign in to {APP_CONFIG.name}
						</p>
					</div>

					{/* Form Validation Errors */}
					{Object.keys(errors).length > 0 && <FormError errors={errors} />}
					
					{/* API Error */}
					{loginError && (
						<div className={`p-4 rounded-xl ${theme.alertError}`}>
							<div className="flex items-start space-x-3">
								<div className={`flex-shrink-0 ${theme.textError}`}>
									<svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
										<path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
									</svg>
								</div>
								<div className="flex-1">
									<h4 className={`text-sm font-medium ${theme.textErrorHeading}`}>
										Login Failed
									</h4>
									<p className={`mt-1 text-sm ${theme.textError}`}>
										{loginError}
									</p>
								</div>
							</div>
						</div>
					)}
					
					<form onSubmit={handleSubmit} className='space-y-4'>
						{/* Username/Email Field */}
						<div className="form-control">
							<Input
								type='text'
								id='email'
								name='email'
								placeholder="Enter your username or email"
								value={formData.email}
								onChange={handleChange}
								error={errors.email}
								required
								className={`w-full px-4 py-3 rounded-xl ${theme.input} ${theme.textPrimary} ${theme.placeholderText} transition-all duration-300 focus:scale-[1.01] focus:ring-2 focus:ring-blue-500/50`}
							/>
						</div>

						{/* Password Field */}
						<div className="form-control">
							<Input
								type='password'
								id='password'
								name='password'
								placeholder="Enter your password"
								value={formData.password}
								onChange={handleChange}
								error={errors.password}
								required
								className={`w-full px-4 py-3 rounded-xl ${theme.input} ${theme.textPrimary} ${theme.placeholderText} transition-all duration-300 focus:scale-[1.01] focus:ring-2 focus:ring-blue-500/50`}
							/>
						</div>

						{/* Forgot Password Link */}
						<div className="text-right -mt-2">
							<Link
								to={ROUTES.FORGOT_PASSWORD}
								className={`text-sm font-medium hover:underline ${theme.textAccent}`}
							>
								Forgot password?
							</Link>
						</div>

						{/* Submit Button */}
						<button
							type="submit"
							disabled={isSubmitting}
							className={`w-full py-3 px-6 rounded-xl font-semibold transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] flex items-center justify-center ${
								isSubmitting ? `${theme.buttonDisabled} ${theme.buttonTextDisabled}` : `${theme.buttonPrimary} ${theme.buttonTextPrimary}`
							}`}
						>
							{isSubmitting ? (
								<span className='flex items-center justify-center gap-2'>
									<svg className='animate-spin h-5 w-5' fill='none' viewBox='0 0 24 24'>
										<circle className='opacity-25' cx='12' cy='12' r='10' stroke='currentColor' strokeWidth='4'></circle>
										<path className='opacity-75' fill='currentColor' d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'></path>
									</svg>
									<span>Signing in...</span>
								</span>
							) : (
								<span>Sign In</span>
							)}
						</button>
					</form>

					{/* Divider */}
					<div className='relative py-3'>
						<div className='absolute inset-0 flex items-center'>
							<div className={`w-full border-t ${theme.separator}`}></div>
						</div>
						<div className='relative flex justify-center text-sm'>
							<span className={`px-4 py-1 ${theme.card} ${theme.textSecondary} rounded-full`}>
								Don't have an account?
							</span>
						</div>
					</div>

					{/* Register Link */}
					<Link
						to={ROUTES.REGISTER}
						className={`block w-full py-3 px-6 rounded-xl font-semibold transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] flex items-center justify-center ${theme.buttonSecondary} ${theme.buttonTextSecondary}`}
					>
						Create New Account
					</Link>

					{/* Footer */}
					<div className={`text-center text-xs ${theme.textMuted} pt-1 leading-relaxed`}>
						By signing in, you agree to our Terms of Service and Privacy Policy
					</div>
				</div>
			</div>
		</div>
	);
}

export default LoginPage;
